import { registerPageInit } from '../barba/page-init-registry';
import '../../css/_root.scss';
import '../../css/pages/residency/residency-page.scss';
import '../../css/templates/residency-header.scss';
import '../../css/pages/residency/residency.scss';
import '../../css/pages/residency/residency-conditions-shared.scss';
import '../../css/pages/residency/residency-ecosystem.scss';
import '../../css/pages/residency/residency-benefits.scss';
import '../../css/pages/residency/residency-advantages.scss';
import '../../css/pages/residency/residency-tax-example.scss';
import '../../css/pages/residency/residency-requirements.scss';
import '../../css/pages/residency/residency-resideents.scss';
import '../../css/pages/residency/residency-documents.scss';
import '../../css/pages/residency/resident-card.scss';
import '../../css/pages/residency/resident-title.scss';
import '../../css/pages/residency/residency-resident-modal.scss';
import { DocumentsSearchWidget } from '../widgets/documents-search-widget';
import { ResidencyResidentModalWidget } from '../widgets/residency-resident-modal-widget';
import { bitrixAjaxFetch } from '../utils/bitrix-ajax';
import { initSearchInput } from '../utils/search-input';
import { initStcSelect } from '../utils/stc-select';
import { updateResidentsFiltersSummaries } from '../utils/residents-filters-summary';
import { initResidencyResidentsFilter } from '../widgets/residency-residents-filter';

const ROOT_SELECTOR = '[data-barba-namespace="residency"]';
const VALID_TABS = ['conditions', 'residents'];
const RESIDENTS_PANEL_SELECTOR = '[data-residency-panel="residents"]';
const RESIDENTS_ROOT_SELECTOR = '[data-residency-residents]';
const RESIDENTS_FILTERS_SELECTOR = '[data-residency-residents-filters]';
const RESIDENTS_PAGINATION_SELECTOR = '.stc-pagination__link, .stc-pagination button[name]';
const RESIDENTS_SEARCH_ID = 'residencyResidentsSearch';
const RESIDENTS_DOCUMENTS_SEARCH_ID = 'residencyDocumentsSearch';
const RESIDENTS_SEARCH_MIN_LENGTH = 3;

let currentTab = '';
let residentsAjaxRequest: AbortController | null = null;
let ensureResidentsTabActive: (() => void) | null = null;
let residencyResidentModal: ResidencyResidentModalWidget | null = null;

function initResidencyResidentModal(root: ParentNode = document): void {
  if (!residencyResidentModal) {
    residencyResidentModal = new ResidencyResidentModalWidget();
  }
  residencyResidentModal.init(root);
}

function parseTab(): string {
  const params = new URLSearchParams(window.location.search || '');
  const tab = params.get('tab')?.trim().toLowerCase();
  if (tab && VALID_TABS.includes(tab)) return tab;
  return 'conditions';
}

function tabUrl(tab: string): string {
  const url = new URL(window.location.href);
  if (tab === 'conditions') url.searchParams.delete('tab');
  else url.searchParams.set('tab', tab);
  return `${url.pathname}${url.search}${url.hash}`;
}

function isResidencyPagePath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, '').toLowerCase();
  return (
    normalized.endsWith('/residency.html') ||
    normalized.endsWith('/residency') ||
    normalized === 'residency.html'
  );
}

function parseResidencyTabFromHref(href: string): string | null {
  try {
    const url = new URL(href, window.location.href);
    if (!isResidencyPagePath(url.pathname)) return null;

    const tab = url.searchParams.get('tab')?.trim().toLowerCase();
    if (tab && VALID_TABS.includes(tab)) return tab;
    return 'conditions';
  } catch {
    return null;
  }
}

function applyResidencyTabDom(activeTab: string): boolean {
  const root = document.querySelector<HTMLElement>(ROOT_SELECTOR);
  if (!root) return false;

  const tabButtons = root.querySelectorAll<HTMLButtonElement>('[data-residency-tab]');
  const panels = root.querySelectorAll<HTMLElement>('[data-residency-panel]');
  if (!tabButtons.length || !panels.length) return false;

  tabButtons.forEach((button) => {
    const isActive = button.dataset.residencyTab === activeTab;
    button.classList.toggle('active', isActive);
    button.setAttribute('aria-selected', isActive ? 'true' : 'false');

    if (isActive) {
      button.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  });

  panels.forEach((panel) => {
    const isActive = panel.dataset.residencyPanel === activeTab;
    panel.classList.toggle('hidden', !isActive);
  });

  currentTab = activeTab;
  return true;
}

function updateResidencyTabHistory(activeTab: string, mode: 'push' | 'replace'): void {
  const url = tabUrl(activeTab);
  const previousState = window.history.state;
  const nextState =
    previousState && typeof previousState === 'object'
      ? { ...previousState, tab: activeTab }
      : { tab: activeTab };

  if (mode === 'replace') {
    window.history.replaceState(nextState, '', url);
  } else {
    window.history.pushState(nextState, '', url);
  }
}

function applyResidencyTab(
  activeTab: string,
  opts?: { push?: boolean; replace?: boolean; updateHistory?: boolean },
): void {
  if (!applyResidencyTabDom(activeTab)) return;

  if (opts?.push) {
    updateResidencyTabHistory(activeTab, 'push');
  } else if (opts?.replace && opts.updateHistory !== false) {
    updateResidencyTabHistory(activeTab, 'replace');
  }
}

function getActiveResidencyTabFromDom(): string {
  const root = document.querySelector<HTMLElement>(ROOT_SELECTOR);
  const activeButton = root?.querySelector<HTMLElement>('[data-residency-tab].active');
  return activeButton?.dataset.residencyTab ?? currentTab;
}

function syncResidencyTabFromUrl(): void {
  applyResidencyTabDom(parseTab());
}

function scheduleResidencyTabSyncFromUrl(): void {
  syncResidencyTabFromUrl();
  queueMicrotask(syncResidencyTabFromUrl);
  requestAnimationFrame(syncResidencyTabFromUrl);
}

let residencyTabNavBound = false;

function ensureResidencyTabNavigationHandlers(): void {
  if (residencyTabNavBound) return;
  residencyTabNavBound = true;

  window.addEventListener('popstate', () => {
    if (!document.querySelector(ROOT_SELECTOR)) return;
    syncResidencyTabFromUrl();
  });

  window.addEventListener('stc:barba-after-enter', () => {
    if (!document.querySelector(ROOT_SELECTOR)) return;
    scheduleResidencyTabSyncFromUrl();
  });

  window.addEventListener('stc:barba-before-leave', () => {
    if (!document.querySelector(ROOT_SELECTOR)) return;
    currentTab = '';
  });

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const tabButton = target.closest<HTMLButtonElement>('[data-residency-tab]');
      if (tabButton?.closest(ROOT_SELECTOR)) {
        const tab = tabButton.dataset.residencyTab;
        if (!tab || tab === getActiveResidencyTabFromDom()) return;

        event.preventDefault();
        applyResidencyTab(tab, { push: true });
        return;
      }

      const link = target.closest<HTMLAnchorElement>('a[href]');
      if (!link) return;

      const tabFromHref = parseResidencyTabFromHref(link.href);
      if (tabFromHref === null) return;
      if (!document.querySelector(ROOT_SELECTOR)) return;

      const url = new URL(link.href, window.location.href);
      if (url.pathname !== window.location.pathname) return;
      if (tabFromHref === getActiveResidencyTabFromDom()) return;

      event.preventDefault();
      applyResidencyTab(tabFromHref, { push: true });
    },
    true,
  );
}

function initResidencyTabs(): void {
  ensureResidencyTabNavigationHandlers();

  ensureResidentsTabActive = () => {
    applyResidencyTab('residents', { replace: true });
  };

  scheduleResidencyTabSyncFromUrl();
}

function debounce(fn: () => void, delay: number): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;

  return () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fn, delay);
  };
}

function syncResidentsFiltersFromUrl(root: ParentNode): void {
  const params = new URLSearchParams(window.location.search);
  const form = root.querySelector<HTMLFormElement>(RESIDENTS_FILTERS_SELECTOR);
  if (!form) return;

  Array.from(form.elements).forEach((element) => {
    if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement)) {
      return;
    }

    const name = element.name;
    if (!name) return;

    element.value = params.get(name) ?? '';
  });
}

function addFormValueToUrl(url: URL, name: string, value: FormDataEntryValue): void {
  if (typeof value !== 'string') return;

  const trimmedValue = value.trim();
  if (trimmedValue) {
    url.searchParams.set(name, trimmedValue);
    return;
  }

  url.searchParams.delete(name);
}

function addResidentsSearchToUrl(url: URL, form: HTMLFormElement): void {
  const searchValue = form.querySelector<HTMLInputElement>(`#${RESIDENTS_SEARCH_ID}`)?.value ?? '';
  addFormValueToUrl(url, 'q', searchValue);
}

function buildResidentsFilterUrl(form: HTMLFormElement): URL {
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('tab', 'residents');

  new FormData(form).forEach((value, name) => {
    addFormValueToUrl(url, name, value);
  });
  addResidentsSearchToUrl(url, form);

  return url;
}

function mergeResidentsFiltersIntoUrl(url: URL, form: HTMLFormElement): URL {
  url.searchParams.set('tab', 'residents');

  new FormData(form).forEach((value, name) => {
    if (!url.searchParams.has(name)) {
      addFormValueToUrl(url, name, value);
    }
  });
  if (!url.searchParams.has('q')) {
    addResidentsSearchToUrl(url, form);
  }

  return url;
}

function findResidentsReplaceTarget(doc: Document): HTMLElement | null {
  return (
    doc.querySelector<HTMLElement>(`${RESIDENTS_ROOT_SELECTOR} > .container`) ??
    doc.querySelector<HTMLElement>('.residency-residents > .container') ??
    doc.querySelector<HTMLElement>('.residency-residents .container') ??
    doc.querySelector<HTMLElement>(RESIDENTS_ROOT_SELECTOR) ??
    doc.querySelector<HTMLElement>('.residency-residents__cluster')?.closest('.container') ??
    doc.querySelector<HTMLElement>('.container')
  );
}

function isPersistentResidentsNode(node: Element): boolean {
  if (!(node instanceof HTMLElement)) {
    return false;
  }

  return (
    node.matches('[data-search-summary][data-search-for="residencyResidentsSearch"]') ||
    node.hasAttribute('data-activity-filters-summary') ||
    node.hasAttribute('data-residents-empty')
  );
}

function shouldSkipResidentsResultNode(node: Element): boolean {
  return isPersistentResidentsNode(node);
}

function getResidentsResultsInsertAnchor(form: HTMLElement): Element {
  let anchor: Element = form;
  let node = form.nextElementSibling;

  while (node && isPersistentResidentsNode(node)) {
    anchor = node;
    node = node.nextElementSibling;
  }

  return anchor;
}

function replaceResidentsResults(currentContainer: HTMLElement, nextContainer: HTMLElement): void {
  const currentForm = currentContainer.querySelector<HTMLElement>(RESIDENTS_FILTERS_SELECTOR);
  const nextForm = nextContainer.querySelector<HTMLElement>(RESIDENTS_FILTERS_SELECTOR);

  if (!currentForm || !nextForm) {
    currentContainer.replaceWith(document.importNode(nextContainer, true));
    return;
  }

  let node = currentForm.nextElementSibling;
  while (node) {
    const next = node.nextElementSibling;
    if (!isPersistentResidentsNode(node)) {
      node.remove();
    }
    node = next;
  }

  let insertAfter = getResidentsResultsInsertAnchor(currentForm);
  let sourceNode = nextForm.nextElementSibling;
  while (sourceNode) {
    const nextSource = sourceNode.nextElementSibling;
    if (!shouldSkipResidentsResultNode(sourceNode)) {
      const cloned = document.importNode(sourceNode, true);
      insertAfter.insertAdjacentElement('afterend', cloned);
      insertAfter = cloned;
    }
    sourceNode = nextSource;
  }
}

function shouldSubmitResidentsSearch(value: string, lastSubmittedSearch: string): boolean {
  const trimmed = value.trim();

  if (!trimmed) {
    return true;
  }

  if (trimmed.length >= RESIDENTS_SEARCH_MIN_LENGTH) {
    return true;
  }

  return lastSubmittedSearch.trim().length >= RESIDENTS_SEARCH_MIN_LENGTH;
}

function getPaginationPageParam(nav: Element | null): string | undefined {
  if (!nav) return undefined;

  const fromData = nav instanceof HTMLElement ? nav.dataset.pageParam : undefined;
  if (fromData?.startsWith('nav-section-nav-section-')) {
    return fromData.replace('nav-section-nav-section-', 'nav-section-');
  }
  if (fromData) return fromData;

  const match = nav.id.match(/^pagination-(nav-section-\d+)-residents-pagination$/);
  return match?.[1];
}

function buildResidentsPaginationUrl(form: HTMLFormElement, pageParam: string, page: number): URL {
  const url = new URL(window.location.href);
  url.searchParams.set(pageParam, `page-${page}`);
  return mergeResidentsFiltersIntoUrl(url, form);
}

function resolveResidentsPaginationUrl(
  control: HTMLAnchorElement,
  form: HTMLFormElement,
): URL | null {
  const href = control.getAttribute('href');
  if (href && href !== '#') {
    return mergeResidentsFiltersIntoUrl(new URL(href, window.location.origin), form);
  }

  const page = Number(control.dataset.page);
  const pageParam = getPaginationPageParam(control.closest('.stc-pagination'));
  if (pageParam && Number.isFinite(page) && page >= 1) {
    return buildResidentsPaginationUrl(form, pageParam, page);
  }

  return null;
}

type ResidentsAjaxUpdateOptions = {
  scrollToClusterPageParam?: string;
};

function getResidentsScrollOffset(): number {
  const header =
    document.querySelector<HTMLElement>('.header-desktop') ??
    document.querySelector<HTMLElement>('.header') ??
    document.querySelector<HTMLElement>('header');

  return (header?.getBoundingClientRect().height ?? 72) + 24;
}

function findResidentsClusterByPageParam(
  residents: HTMLElement,
  pageParam: string,
): HTMLElement | null {
  const paginations = Array.from(
    residents.querySelectorAll<HTMLElement>('.residency-residents__pagination[data-page-param]'),
  );

  for (const pagination of paginations) {
    if (getPaginationPageParam(pagination) === pageParam) {
      return pagination.closest('.residency-residents__cluster') as HTMLElement | null;
    }
  }

  return null;
}

function scrollToResidentsCluster(root: HTMLElement, pageParam: string): void {
  const residents = root.querySelector<HTMLElement>(RESIDENTS_ROOT_SELECTOR);
  if (!residents) return;

  const cluster = findResidentsClusterByPageParam(residents, pageParam);
  if (!cluster) return;

  requestAnimationFrame(() => {
    const offset = getResidentsScrollOffset();
    const top = cluster.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  });
}

async function updateResidentsFromAjax(
  root: HTMLElement,
  url: URL,
  options: ResidentsAjaxUpdateOptions = {},
): Promise<void> {
  const currentResidents = root.querySelector<HTMLElement>(RESIDENTS_ROOT_SELECTOR);
  if (!currentResidents) return;

  const currentTarget =
    currentResidents.querySelector<HTMLElement>(':scope > .container') ?? currentResidents;

  residentsAjaxRequest?.abort();
  const abortController = new AbortController();
  residentsAjaxRequest = abortController;

  currentResidents.classList.add('is-loading');
  currentResidents.setAttribute('aria-busy', 'true');

  try {
    const response = await bitrixAjaxFetch(url.toString(), {
      method: 'GET',
      signal: abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Residents request failed: ${response.status}`);
    }

    const html = await response.text();
    if (abortController.signal.aborted) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextTarget = findResidentsReplaceTarget(doc);

    if (!nextTarget) {
      throw new Error('Residents block was not found in ajax response');
    }

    replaceResidentsResults(currentTarget, nextTarget);
    window.history.replaceState(
      { tab: 'residents' },
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
    ensureResidentsTabActive?.();

    markResidentsPaginationBarbaSafe(
      root.querySelector<HTMLElement>(RESIDENTS_PANEL_SELECTOR) ?? root,
    );
    initResidencyResidentModal(root);
    updateResidentsFiltersSummaries(root);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return;
    }

    console.error(error);
  } finally {
    if (residentsAjaxRequest === abortController) {
      residentsAjaxRequest = null;
    }

    const residents = root.querySelector<HTMLElement>(RESIDENTS_ROOT_SELECTOR);
    residents?.classList.remove('is-loading');
    residents?.removeAttribute('aria-busy');

    if (options.scrollToClusterPageParam) {
      scrollToResidentsCluster(root, options.scrollToClusterPageParam);
    }
  }
}

function markResidentsPaginationBarbaSafe(panel: ParentNode): void {
  panel
    .querySelectorAll<HTMLElement>('.stc-pagination, .stc-pagination__link')
    .forEach((element) => {
      element.setAttribute('data-barba-prevent', '');
    });
}

function initResidencyResidentsAjax(root: HTMLElement): void {
  const panel = root.querySelector<HTMLElement>(RESIDENTS_PANEL_SELECTOR);
  const form = panel?.querySelector<HTMLFormElement>(RESIDENTS_FILTERS_SELECTOR);
  if (!panel || !form) return;

  markResidentsPaginationBarbaSafe(panel);
  syncResidentsFiltersFromUrl(panel);
  initSearchInput(panel, { inputId: RESIDENTS_SEARCH_ID, mode: 'server', urlParam: 'q' });
  initStcSelect(panel);
  bindResidentsFilters(panel, form, root);
  updateResidentsFiltersSummaries(root);
}

function bindResidentsFilters(panel: HTMLElement, form: HTMLFormElement, root: HTMLElement): void {
  if (form.dataset.residentsFiltersBound !== 'true') {
    form.dataset.residentsFiltersBound = 'true';

    let ready = false;
    let lastSubmittedSearch = '';
    const searchInput = form.querySelector<HTMLInputElement>(`#${RESIDENTS_SEARCH_ID}`);

    const submitFilters = (): void => {
      if (!ready) return;
      lastSubmittedSearch = searchInput?.value ?? '';
      void updateResidentsFromAjax(root, buildResidentsFilterUrl(form));
    };
    const submitFiltersDebounced = debounce(submitFilters, 400);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submitFilters();
    });

    searchInput?.addEventListener('input', () => {
      const value = searchInput.value;
      if (!shouldSubmitResidentsSearch(value, lastSubmittedSearch)) {
        return;
      }

      submitFiltersDebounced();
    });

    form.querySelector<HTMLButtonElement>('[data-search-clear]')?.addEventListener('click', () => {
      queueMicrotask(submitFilters);
    });

    form.querySelectorAll<HTMLSelectElement>('select').forEach((select) => {
      select.addEventListener('change', submitFilters);
    });

    queueMicrotask(() => {
      ready = true;
    });
  }

  if (panel.dataset.residentsPaginationBound !== 'true') {
    panel.dataset.residentsPaginationBound = 'true';

    panel.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const control = target.closest<HTMLElement>(RESIDENTS_PAGINATION_SELECTOR);
        if (!control || !panel.contains(control)) return;
        if (control.classList.contains('is-active')) return;

        event.preventDefault();
        event.stopPropagation();

        const currentForm = panel.querySelector<HTMLFormElement>(RESIDENTS_FILTERS_SELECTOR);
        if (!currentForm) return;

        const paginationNav = control.closest('.stc-pagination');
        const scrollToClusterPageParam = getPaginationPageParam(paginationNav);

        if (control instanceof HTMLAnchorElement) {
          const url = resolveResidentsPaginationUrl(control, currentForm);
          if (!url) return;

          void updateResidentsFromAjax(root, url, { scrollToClusterPageParam });
          return;
        }

        if (control instanceof HTMLButtonElement && control.name) {
          const url = buildResidentsFilterUrl(currentForm);
          addFormValueToUrl(url, control.name, control.value);
          void updateResidentsFromAjax(root, url, {
            scrollToClusterPageParam: getPaginationPageParam(paginationNav) ?? control.name,
          });
        }
      },
      true,
    );
  }
}

function initResidencyDocumentsSearch(root: HTMLElement): void {
  initSearchInput(root, { inputId: RESIDENTS_DOCUMENTS_SEARCH_ID });

  new DocumentsSearchWidget(
    RESIDENTS_DOCUMENTS_SEARCH_ID,
    'inner-residencyDocsSearchResult',
    'residencyAccordionSearchResult',
    'residencyDocsSearchResultSummary',
    'residencyDocsSearchResult-no-matches',
    'residencyDocumentsAccordionGroup',
    3,
    400,
    'residents',
  ).init();
}

registerPageInit('residency', () => {
  const root = document.querySelector<HTMLElement>(ROOT_SELECTOR);
  if (!root) return;
  initResidencyTabs();
  initResidencyDocumentsSearch(root);
  initResidencyResidentsAjax(root);
  initResidencyResidentModal(root);
  initResidencyResidentsFilter(root);
  initStcSelect(root);
});
